import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import {
  CONFLICTS_POLICIES,
  roleCanManageSettings,
  type AppSettings,
  type ConflictsPolicy,
  type SettingsResponse,
} from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Draft {
  lockTimeoutMinutes: string;
  tdCodeGraceHours: string;
  conflictsPolicy: ConflictsPolicy;
  notifyEmails: string;
}

const EMPTY: Draft = {
  lockTimeoutMinutes: '30',
  tdCodeGraceHours: '24',
  conflictsPolicy: 'warn',
  notifyEmails: '',
};

export function SettingsPage(): JSX.Element {
  const { user } = useAuth();
  const canManage = user ? roleCanManageSettings(user.role) : false;
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(canManage);

  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<SettingsResponse>('/api/settings');
        if (!cancelled) setDraft(toDraft(body.settings));
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(caught instanceof ApiClientError ? caught.message : 'Could not load settings.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManage]);

  if (!canManage) {
    return (
      <section className="card" data-testid="settings-page">
        <h2>Settings</h2>
        <p className="status status--error" data-testid="settings-denied">
          Only an admin or equipment admin can change settings.
        </p>
      </section>
    );
  }

  async function onSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setNotice(null);
    const lockTimeoutMinutes = Number(draft.lockTimeoutMinutes);
    const tdCodeGraceHours = Number(draft.tdCodeGraceHours);
    try {
      const body = await apiRequest<SettingsResponse>('/api/settings', {
        method: 'PATCH',
        body: {
          lockTimeoutMinutes,
          tdCodeGraceHours,
          conflictsPolicy: draft.conflictsPolicy,
          notifyEmails: emailsFromText(draft.notifyEmails),
        },
      });
      setDraft(toDraft(body.settings));
      setNotice('Settings saved.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not save settings.');
    }
  }

  return (
    <section data-testid="settings-page">
      <div className="card">
        <h2>Settings</h2>
        <p className="meta">
          Lock timeout, tournament-director code grace, overlapping-assignment policy, and extra
          notify recipients. Changes are audited.
        </p>
        {error ? (
          <p className="status status--error" data-testid="settings-error">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="status status--ok" data-testid="settings-notice">
            {notice}
          </p>
        ) : null}
      </div>

      {loading ? <p className="meta">Loading settings…</p> : null}

      <form className="card" onSubmit={(event) => void onSave(event)} data-testid="settings-form">
        <label className="field">
          Lock timeout (minutes)
          <input
            type="number"
            min={1}
            max={1440}
            required
            value={draft.lockTimeoutMinutes}
            onChange={(event) =>
              setDraft((current) => ({ ...current, lockTimeoutMinutes: event.target.value }))
            }
            data-testid="settings-lock-timeout"
          />
        </label>
        <label className="field">
          Tournament-director code grace (hours)
          <input
            type="number"
            min={0}
            max={336}
            required
            value={draft.tdCodeGraceHours}
            onChange={(event) =>
              setDraft((current) => ({ ...current, tdCodeGraceHours: event.target.value }))
            }
            data-testid="settings-grace-hours"
          />
        </label>
        <label className="field">
          Overlapping assignments
          <select
            value={draft.conflictsPolicy}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                conflictsPolicy: event.target.value as ConflictsPolicy,
              }))
            }
            data-testid="settings-conflicts-policy"
          >
            {CONFLICTS_POLICIES.map((policy) => (
              <option key={policy} value={policy}>
                {policy === 'warn' ? 'Warn and allow save' : 'Block overlapping saves'}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Extra notify emails
          <textarea
            value={draft.notifyEmails}
            onChange={(event) =>
              setDraft((current) => ({ ...current, notifyEmails: event.target.value }))
            }
            data-testid="settings-notify-emails"
            placeholder="one address per line"
          />
        </label>
        <button type="submit" className="button" data-testid="settings-save">
          Save settings
        </button>
      </form>
    </section>
  );
}

function toDraft(settings: AppSettings): Draft {
  return {
    lockTimeoutMinutes: String(settings.lockTimeoutMinutes),
    tdCodeGraceHours: String(settings.tdCodeGraceHours),
    conflictsPolicy: settings.conflictsPolicy,
    notifyEmails: settings.notifyEmails.join('\n'),
  };
}

function emailsFromText(value: string): string[] {
  return value
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}
